import React, { useState, useEffect } from 'react';
import { 
  BarChart3, TrendingUp, Eye, Calendar, Clock, 
  RefreshCw, ChevronLeft, Users, Globe
} from 'lucide-react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AnalyticsPanel = ({ password, onBack }) => {
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/analytics/stats`, {
        headers: { 'X-Admin-Password': password }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Analytics load error:', error);
      toast.error('Fehler beim Laden der Statistiken');
    } finally {
      setIsLoading(false);
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('de-DE').format(num || 0);
  };

  // Calculate max for chart scaling
  const maxDailyViews = stats?.dailyViews ? Math.max(...stats.dailyViews.map(d => d.views), 1) : 1;
  const maxHourlyViews = stats?.hourlyViews ? Math.max(...stats.hourlyViews.map(h => h.views), 1) : 1;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Statistiken werden geladen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center">
            <div>
              <button 
                onClick={onBack}
                className="text-indigo-200 hover:text-white flex items-center gap-1 mb-2 text-sm"
              >
                <ChevronLeft className="h-4 w-4" /> Zurück zum Admin
              </button>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <BarChart3 className="h-7 w-7" />
                Besucherstatistiken
              </h1>
              <p className="text-indigo-200">Anonymisierte Seitenaufrufe (DSGVO-konform)</p>
            </div>
            <Button variant="secondary" size="sm" onClick={loadStats}>
              <RefreshCw className="h-4 w-4 mr-2" /> Aktualisieren
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6 space-y-6">
        {/* DSGVO Hinweis */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-green-800 text-sm flex items-center gap-2">
            <span className="text-lg">🔒</span>
            <span>
              <strong>DSGVO-konform:</strong> Es werden ausschließlich anonymisierte Seitenaufrufe gezählt. 
              Keine IP-Adressen, keine Cookies, keine persönlichen Daten.
            </span>
          </p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-blue-100 text-sm">Heute</p>
                  <p className="text-3xl font-bold">{formatNumber(stats?.today)}</p>
                </div>
                <Eye className="h-8 w-8 text-blue-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-purple-100 text-sm">Gestern</p>
                  <p className="text-3xl font-bold">{formatNumber(stats?.yesterday)}</p>
                </div>
                <Calendar className="h-8 w-8 text-purple-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-100 text-sm">Diese Woche</p>
                  <p className="text-3xl font-bold">{formatNumber(stats?.thisWeek)}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-green-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-orange-100 text-sm">Dieser Monat</p>
                  <p className="text-3xl font-bold">{formatNumber(stats?.thisMonth)}</p>
                </div>
                <Users className="h-8 w-8 text-orange-200" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-gray-700 to-gray-800 text-white">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-300 text-sm">Gesamt</p>
                  <p className="text-3xl font-bold">{formatNumber(stats?.total)}</p>
                </div>
                <Globe className="h-8 w-8 text-gray-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* Daily Views Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-blue-600" />
                Letzte 14 Tage
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-end justify-between gap-1 h-48">
                {stats?.dailyViews?.map((day, idx) => (
                  <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                    <div className="w-full flex justify-center items-end h-36">
                      <div 
                        className="w-full max-w-[24px] bg-gradient-to-t from-blue-600 to-blue-400 rounded-t transition-all hover:from-blue-700 hover:to-blue-500"
                        style={{ 
                          height: `${Math.max((day.views / maxDailyViews) * 100, day.views > 0 ? 5 : 0)}%`,
                          minHeight: day.views > 0 ? '4px' : '0'
                        }}
                        title={`${day.label}: ${day.views} Aufrufe`}
                      />
                    </div>
                    <span className="text-xs text-gray-500 font-medium">{day.views}</span>
                    <span className="text-[10px] text-gray-400">{day.label}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Hourly Views Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-purple-600" />
                Heute nach Uhrzeit
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-end justify-between gap-[2px] h-48">
                {stats?.hourlyViews?.map((hour, idx) => (
                  <div key={idx} className="flex-1 flex flex-col items-center">
                    <div className="w-full flex justify-center items-end h-36">
                      <div 
                        className="w-full max-w-[16px] bg-gradient-to-t from-purple-600 to-purple-400 rounded-t transition-all hover:from-purple-700 hover:to-purple-500"
                        style={{ 
                          height: `${Math.max((hour.views / maxHourlyViews) * 100, hour.views > 0 ? 5 : 0)}%`,
                          minHeight: hour.views > 0 ? '4px' : '0'
                        }}
                        title={`${hour.label}: ${hour.views} Aufrufe`}
                      />
                    </div>
                    {idx % 3 === 0 && (
                      <span className="text-[9px] text-gray-400 mt-1">{hour.label}</span>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Top Pages */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-green-600" />
              Meistbesuchte Seiten
            </CardTitle>
          </CardHeader>
          <CardContent>
            {stats?.topPages?.length === 0 ? (
              <p className="text-gray-500 text-center py-8">Noch keine Daten verfügbar</p>
            ) : (
              <div className="space-y-3">
                {stats?.topPages?.map((page, idx) => {
                  const maxPageViews = stats.topPages[0]?.views || 1;
                  const percentage = (page.views / maxPageViews) * 100;
                  
                  // Seiten-Namen übersetzen
                  const pageNames = {
                    'home': 'Startseite',
                    'services': 'Leistungen',
                    'pricing': 'Preise',
                    'flatrate': 'Pfotenflatrate',
                    'booking': 'Buchung',
                    'about': 'Über mich',
                    'contact': 'Kontakt',
                    'gallery': 'Galerie',
                    'reviews': 'Bewertungen',
                    'agb': 'AGB',
                    'datenschutz': 'Datenschutz',
                    'impressum': 'Impressum'
                  };
                  const displayName = pageNames[page.page] || page.page;
                  
                  return (
                    <div key={idx} className="flex items-center gap-3">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                        idx === 0 ? 'bg-yellow-400 text-yellow-900' :
                        idx === 1 ? 'bg-gray-300 text-gray-700' :
                        idx === 2 ? 'bg-orange-300 text-orange-800' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {idx + 1}
                      </span>
                      <div className="flex-1">
                        <div className="flex justify-between items-center mb-1">
                          <span className="font-medium text-gray-800">{displayName}</span>
                          <span className="text-sm text-gray-600">{formatNumber(page.views)} Aufrufe</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-green-500 to-green-400 h-2 rounded-full transition-all"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Footer */}
        <div className="text-center text-sm text-gray-500 py-4">
          <p>Letzte Aktualisierung: {stats?.lastUpdated ? new Date(stats.lastUpdated).toLocaleString('de-DE') : '-'}</p>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPanel;
